/**
 * Google Analytics 4 Integration
 *
 * This module provides utilities for tracking user interactions with Google Analytics.
 * Events are sent to GA4 to measure user engagement and conversion rates.
 */

// Get GA Measurement ID from environment
const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

/**
 * Initialize Google Analytics
 * This should be called once when the app loads
 */
export const initGA = () => {
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
    return;
  }

  // Check if gtag is available
  if (typeof window.gtag !== 'function') {
    return;
  }

  // Initialize GA4
  window.gtag('js', new Date());
  window.gtag('config', GA_MEASUREMENT_ID, {
    send_page_view: true,
    cookie_flags: 'SameSite=None;Secure',
  });
};

/**
 * Track a page view
 * @param {string} path - The page path
 * @param {string} title - The page title
 */
export const trackPageView = (path, title) => {
  if (!isGAEnabled()) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
};

/**
 * Track a custom event
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters
 */
export const trackEvent = (eventName, eventParams = {}) => {
  if (!isGAEnabled()) return;

  window.gtag('event', eventName, eventParams);
};

/**
 * Track external link clicks (most important for academy banner)
 * @param {string} url - The destination URL
 * @param {string} source - Where the click originated from
 */
export const trackOutboundLink = (url, source = 'unknown') => {
  if (!isGAEnabled()) return;

  window.gtag('event', 'click', {
    event_category: 'outbound',
    event_label: url,
    event_source: source,
    transport_type: 'beacon',
  });
};

/**
 * Track academy promotional banner interactions
 * This is the key metric for measuring traffic to the academy website
 */
export const trackAcademyBannerClick = () => {
  const url = 'https://www.automationacademy.online';

  // Track as outbound link
  trackOutboundLink(url, 'promo_banner');

  // Track as conversion event
  trackEvent('academy_referral', {
    event_category: 'conversion',
    event_label: 'course_promo_popup',
    value: 1,
  });

  // Track as custom event for easier filtering in GA
  trackEvent('visit_academy_website', {
    source: 'promo_popup',
    destination: url,
  });
};

/**
 * Track user registration
 */
export const trackRegistration = (method = 'email') => {
  trackEvent('sign_up', {
    method: method,
  });
};

/**
 * Track user login
 */
export const trackLogin = (method = 'email') => {
  trackEvent('login', {
    method: method,
  });
};

/**
 * Track level completion
 */
export const trackLevelComplete = (levelId, xpEarned) => {
  trackEvent('level_complete', {
    level_id: levelId,
    xp_earned: xpEarned,
  });
};

/**
 * Track achievement unlock
 */
export const trackAchievement = (achievementId, achievementName) => {
  trackEvent('unlock_achievement', {
    achievement_id: achievementId,
    achievement_name: achievementName,
  });
};

/**
 * Track promotional popup interactions
 */
export const trackPromoPopupView = () => {
  trackEvent('promo_popup_view', {
    event_category: 'engagement',
  });
};

export const trackPromoPopupClose = () => {
  trackEvent('promo_popup_close', {
    event_category: 'engagement',
  });
};

/**
 * Check if Google Analytics is enabled and available
 * @returns {boolean}
 */
function isGAEnabled() {
  if (!GA_MEASUREMENT_ID || GA_MEASUREMENT_ID === 'G-XXXXXXXXXX') {
    return false;
  }

  if (typeof window.gtag !== 'function') {
    return false;
  }

  return true;
}

// Export GA_MEASUREMENT_ID for use in other components
export { GA_MEASUREMENT_ID };
